<script lang="ts">
  import Button from '../ui/Button.svelte';
  import Dialog from '../ui/Dialog.svelte';
  import TutorialArt from './TutorialArt.svelte';
  import { VARIANT_TITLES, TUTORIAL_PAGES } from '../content/tutorial';
  import type { GameVariant } from '../../shared/config';
  import { POWER_UP_INFO } from '../content/power-ups';
  import { getAppearance } from '../themes/appearance.svelte';

  interface Props {
    open?: boolean;
    /** The round's variant; names the dialog. */
    variant: GameVariant;
    /** The variants whose pages to show, in order. */
    teaching: readonly GameVariant[];
    closeRules: (accepted?: boolean) => void;
  }
  let { open = false, variant, teaching, closeRules }: Props = $props();

  const appearance = getAppearance();
  const animate = $derived(appearance.motionAllowed);

  const pages = $derived(teaching.flatMap((set) => TUTORIAL_PAGES[set]));
  let track = $state<HTMLDivElement | null>(null);
  let current = $state(0);
  /** Set while Next scrolls, so intermediate scroll positions don't flicker the page state. */
  let scrollingTo: number | null = null;
  const last = $derived(current === pages.length - 1);

  // Each opening starts from the first page.
  $effect(() => {
    if (!open) return;
    current = 0;
    scrollingTo = null;
    track?.scrollTo({ left: 0, behavior: 'instant' });
  });

  function pageAt(left: number): number {
    return track && track.clientWidth ? Math.round(left / track.clientWidth) : 0;
  }

  function syncPage(): void {
    if (!track) return;
    const page = pageAt(track.scrollLeft);
    if (scrollingTo !== null) {
      if (page === scrollingTo && track.scrollLeft % track.clientWidth < 1) scrollingTo = null;
      return;
    }
    current = Math.min(page, pages.length - 1);
  }

  function next(): void {
    if (last) {
      closeRules(true);
      return;
    }
    current += 1;
    scrollingTo = current;
    // Smooth unless reduced motion, which forces `scroll-behavior: auto` in style.css.
    track?.scrollTo({ left: current * track.clientWidth });
  }
</script>

<Dialog {open} presentation="sheet" labelledby="rules-title" onDismiss={() => closeRules()}>
  <p class="eyebrow">before your first round</p>
  <!-- svelte-ignore a11y_autofocus (Read the rules from the top before reaching the actions.) -->
  <h2 id="rules-title" tabindex="-1" autofocus>How to play {VARIANT_TITLES[variant]}</h2>
  <!-- A swipe hands the page back to the player, even mid-animation. -->
  <!-- svelte-ignore a11y_no_noninteractive_tabindex (A scrollable region must be keyboard reachable; arrow keys page it.) -->
  <div
    class="pages"
    bind:this={track}
    role="region"
    aria-label="Instructions"
    tabindex="0"
    onscroll={syncPage}
    onpointerdown={() => (scrollingTo = null)}
    ontouchstart={() => (scrollingTo = null)}
    onwheel={() => (scrollingTo = null)}
  >
    {#each pages as page, index (page.title)}
      <section
        class="page"
        aria-roledescription="slide"
        aria-label={`${index + 1} of ${pages.length}`}
        aria-hidden={index !== current}
      >
        <TutorialArt art={page.art} powerUps={page.powerUps} {animate} />
        <h3>{page.title}</h3>
        <p>{page.text}</p>
        {#if page.powerUps}
          <dl>
            {#each page.powerUps as kind (kind)}
              <div>
                <dt>{POWER_UP_INFO[kind].title}</dt>
                <dd>{POWER_UP_INFO[kind].description}</dd>
              </div>
            {/each}
          </dl>
        {/if}
      </section>
    {/each}
  </div>
  {#if pages.length > 1}
    <div class="dots" aria-hidden="true">
      {#each pages as page, index (page.title)}<span class:current={index === current}></span>{/each}
    </div>
  {/if}
  <p class="sr-only" aria-live="polite">Page {current + 1} of {pages.length}</p>
  <div class="row">
    <Button variant="primary" size="big" full onclick={next}>{last ? 'Got It' : 'Next'}</Button><Button
      variant="ghost"
      size="small"
      onclick={() => closeRules()}>Not Now</Button
    >
  </div>
</Dialog>

<style>
  .eyebrow {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin: 0 0 14px;
  }
  h2 {
    margin-bottom: 8px;
  }
  .pages {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    /* Bleed to the sheet's 26px inline padding so a swipe starts anywhere across it. */
    --bleed: 26px;
    margin: 0 calc(-1 * var(--bleed));
  }
  .pages::-webkit-scrollbar {
    display: none;
  }
  .pages:focus-visible {
    outline: var(--theme-focus-width) solid var(--theme-focus);
    outline-offset: -4px;
  }
  .page {
    flex: 0 0 100%;
    scroll-snap-align: center;
    scroll-snap-stop: always;
    box-sizing: border-box;
    padding: 0 var(--bleed);
    text-align: center;
  }
  h3 {
    margin: 12px 0 6px;
    font-size: 21px;
  }
  p,
  dl {
    line-height: 1.65;
    font-size: 14px;
  }
  .page p {
    margin: 0;
  }
  dl {
    margin: 10px 0 0;
    display: grid;
    gap: 4px;
    text-align: left;
  }
  dl div {
    display: flex;
    gap: 8px;
  }
  dt {
    font-weight: 700;
    flex: 0 0 56px;
  }
  dd {
    margin: 0;
  }
  .dots {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin: 18px 0 16px;
  }
  .dots span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.3;
    transition:
      opacity var(--theme-interaction-duration) var(--theme-interaction-easing),
      transform var(--theme-interaction-duration) var(--theme-interaction-easing);
  }
  .dots span.current {
    background: var(--theme-accent);
    opacity: 1;
    transform: scale(1.25);
  }
</style>
